import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Card, Pagination, styled } from '@teko/ui';
import { ObjectMap, SearchParams } from 'types';
import {
  BuildPcModalHeader,
  BuildPcModalContent,
  OrderBy,
  BuildPcModalHeaderProps,
} from '@teko/ecommerce';
import { DiscoveryProduct } from '@teko/discovery';
import { cleanObject } from '@teko/utils';
import { useBuildPcDispatch } from 'contexts';
import {
  useDiscoverySearch,
  useMenuItemById,
  useNewProductCard,
  useTenantConfig,
} from '../../hooks';
import { BuildPcCategory } from '../../repositories';
import { FilterInterface, FilterOptionInterface } from '@teko/types/ecommerce';
import { BuildPcMobileModalContent } from './BuildPcMobileModalContent';

export interface BuildPcModal {
  onHide: () => void;
  category: BuildPcCategory;
  initialSocketOption?: ObjectMap;
}

const PaginationWrapper = styled.div`
  padding: 0.5rem 0;
  display: flex;
  justify-content: center;
  height: 62px;
`;

const StyledPagination = styled(Pagination)`
  ul {
    padding: 0;
  }
`;

const CardMobile = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

export const BuildPcModal: React.FC<BuildPcModal> = ({
  onHide,
  category,
  initialSocketOption,
}: BuildPcModal) => {
  const { isMobile } = useTenantConfig();

  const { convertNewProductCard } = useNewProductCard({
    disableHandlerUnder18: true,
  });
  const { result: menuItem } = useMenuItemById(category.id);
  const configs = menuItem?.configs || {};
  const defaultSearchParams = {
    limit: 16,
    page: 1,
    returnAllVariants: true,
  };
  // TODO: check type searchParams in @teko/utils to change type any here
  const [searchParams, setSearchParams] = useState<ObjectMap>({
    ...defaultSearchParams,
    ...configs,
  });

  const filterParams = {
    ...configs,
    ...{
      returnFilterable: [
        'FILTER_TYPE_BRAND',
        'FILTER_TYPE_PRICE',
        'FILTER_TYPE_ATTRIBUTE',
      ],
    },
  };
  const dispatch = useBuildPcDispatch();
  const { result, isLoading } = useDiscoverySearch(
    !!Object.keys(configs).length ? { ...configs, ...searchParams } : {},
  );
  const { result: filterData } = useDiscoverySearch(
    !!Object.keys(configs).length ? filterParams : {},
  );

  const products = result.products;

  const filters = filterData?.cookedFilters?.slice(0, 6);

  const onSelectProduct = (sku: string): void => {
    if (!products.length) return;

    const product = products.find(
      (product: DiscoveryProduct) => product.productInfo.sku === sku,
    );

    if (!product) return;

    dispatch({
      type: 'ADD_HARDWARE',
      payload: {
        categoryId: category.id,
        product: convertNewProductCard(product),
      },
    });
  };

  const onUpdateParams = (code: string, value: string): void => {
    let params = searchParams;
    if (code !== 'keyword' && params[code]) {
      const filterCodeValues = params[code]?.toString().split(',') || [];
      const index = filterCodeValues.indexOf(value);
      if (index !== -1) {
        filterCodeValues.splice(index, 1);
      } else {
        filterCodeValues.push(value);
      }
      if (filterCodeValues.length) {
        params[code] = filterCodeValues;
      } else {
        const { [code]: tbr, ...restFilterParams } = params;
        params = restFilterParams;
      }
    } else {
      params[code] = value;
    }
    setSearchParams(cleanObject(params));
  };

  const onSort = useCallback(
    (sort: string, order: OrderBy) => {
      let params = searchParams;

      if (sort === searchParams.sort && order === searchParams.order) {
        const { sort: tbr, order: tbr2, ...restFilterParams } = params;
        params = { ...restFilterParams, page: 1 };
      } else {
        params = {
          ...searchParams,
          sort: sort,
          order: order,
          page: 1,
        };
      }
      setSearchParams(cleanObject(params));
    },
    [searchParams],
  );

  const getActivatedFilters = (
    filters: Array<FilterInterface>,
    searchParams: SearchParams,
  ): Array<FilterOptionInterface> => {
    if (!filters || !filters.length) return [];
    const activatedFilters: Array<FilterOptionInterface> = [];
    Object.keys(searchParams).forEach((queryItem) => {
      filters.forEach((filter) => {
        if (filter.code === queryItem) {
          filter.options.forEach((value: FilterOptionInterface) => {
            if (
              !!value.optionCode &&
              (searchParams[queryItem] as string[])?.includes(value.optionCode)
            ) {
              activatedFilters.push(value);
            }
          });
        }
      });
    });
    return activatedFilters;
  };

  const activeValues = useMemo(
    () => getActivatedFilters(filters || [], searchParams),
    [filters, searchParams],
  );

  useEffect(() => {
    const socketOption: FilterOptionInterface | undefined = filters
      ?.find(
        (f) =>
          f.code === initialSocketOption?.filterCode &&
          f.name === initialSocketOption?.filterName,
      )
      ?.options.find(
        (opt) => opt.optionName === initialSocketOption?.optionName,
      );

    const socketSearchParam = {
      [socketOption?.filterCode as string]: socketOption?.optionCode,
    };

    if (!!Object.keys(configs).length)
      setSearchParams(
        cleanObject({
          ...defaultSearchParams,
          ...configs,
          ...socketSearchParam,
        }),
      );
  }, [
    JSON.stringify(configs),
    JSON.stringify(filters),
    JSON.stringify(initialSocketOption),
  ]);

  const renderContent = (
    <>
      <BuildPcModalHeader
        searchParams={searchParams as BuildPcModalHeaderProps['searchParams']}
        onClickSort={onSort}
        onClickFilter={onUpdateParams}
        filters={filters || []}
        activeValues={activeValues}
        isMobile={isMobile}
      />
      {isMobile ? (
        <BuildPcMobileModalContent
          isLoading={isLoading}
          onClick={(sku: string): void => {
            onSelectProduct(sku);
            onHide();
          }}
          products={products.map(convertNewProductCard)}
        />
      ) : (
        <BuildPcModalContent
          isLoading={isLoading}
          onClick={(sku: string): void => {
            onSelectProduct(sku);
            onHide();
          }}
          products={products.map(convertNewProductCard)}
        />
      )}
      <PaginationWrapper>
        <StyledPagination
          totalRecords={result.pagination.totalItems || 0}
          pageLimit={searchParams.limit || 0}
          onPageChanged={(pageNum: number): void => {
            setSearchParams({ ...searchParams, page: pageNum });
          }}
          currentPage={searchParams.page || 1}
          pageNeighbours={isMobile ? 0 : 2}
        />
      </PaginationWrapper>
    </>
  );

  return !isMobile ? (
    <Card>{renderContent}</Card>
  ) : (
    <CardMobile>{renderContent}</CardMobile>
  );
};
